import { Target } from 'lucide-react';
import { weekRangeLabel, weeklyMinimumCopy } from '@/lib/messageQuota';
import { cn } from '@/lib/utils';
import type { MyWeeklyMinimum } from '@/api/types';

/**
 * An outreacher's own week against the message quota their manager set,
 * framed as a minimum: the bar fills to it, and past it the card says so
 * rather than stopping at "done". Renders nothing when no quota is set.
 */
export function WeeklyMinimumCard({ data, className }: { data: MyWeeklyMinimum; className?: string }): JSX.Element | null {
  if (data.minimum === null || data.minimum <= 0) return null;
  const copy = weeklyMinimumCopy(data.messages, data.minimum);
  const done = copy.state !== 'below';

  return (
    <div className={cn('panel rounded-xl p-5', className)}>
      <div className="flex flex-wrap items-start gap-4">
        <span
          aria-hidden
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.625rem] border',
            done ? 'border-success/25 bg-success/10 text-success' : 'border-brand/25 bg-brand/10 text-brand',
          )}
        >
          <Target className="h-5 w-5" strokeWidth={1.85} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">
            Your weekly minimum · {weekRangeLabel(data.weekStart)}
          </p>
          <p className="mt-0.5 text-[0.9375rem] font-bold">{copy.headline}</p>
          <p className="mt-0.5 max-w-[60ch] text-sm text-muted-foreground">{copy.detail}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-2xl font-medium tabular-nums tracking-[-0.04em]">
            {data.messages.toLocaleString()}
            <span className="text-base text-muted-foreground"> / {data.minimum.toLocaleString()}</span>
          </p>
          <p className="text-xs text-muted-foreground">creators messaged</p>
        </div>
      </div>
      <div
        className="mt-4 h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label="Progress to your weekly minimum"
        aria-valuemin={0}
        aria-valuemax={data.minimum}
        aria-valuenow={Math.min(data.messages, data.minimum)}
      >
        <div
          className={cn('h-full rounded-full transition-[width]', done ? 'bg-success' : 'bg-brand')}
          style={{ width: `${copy.percent}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Counts every creator you mark as messaged or log a reply from. Resets Monday.
      </p>
    </div>
  );
}
